/* Enhance the existing widget; Judge.me owns review content and pagination. */
(() => {
  if (customElements.get('customer-reviews')) return;

  customElements.define('customer-reviews', class extends HTMLElement {
    connectedCallback() {
      this.sortedWidgets ??= new WeakSet();
      this.loadingWidgets = new Map();
      this.requests = new Map();
      this.onNavigation = event => {
        const control = event.target.closest(event.type === 'click'
          ? '.jdgm-paginate__page:not(.jdgm-curt), .jdgm-paginate__load-more'
          : '.jdgm-sort-dropdown');
        const widget = control?.closest('.jdgm-review-widget');
        if (widget && this.contains(widget) && (event.type === 'change' || control.closest('.jdgm-rev-widg__body'))) {
          const state = this.loadingWidgets.get(widget);
          if (event !== state?.initialSortEvent) state?.restoreScroll?.();
          this.preserveHeight(widget);
        }
      };
      // Capture before Judge.me's bubbling handler hides the current review body.
      this.addEventListener('click', this.onNavigation, true);
      this.addEventListener('change', this.onNavigation, true);
      this.observer = new MutationObserver(() => this.scheduleUpdate());
      this.update();
    }

    preserveHeight(widget) {
      if (!this.providerDocument || !widget.classList.contains('jdgm--done-setup-widget') || this.loadingWidgets.has(widget)) return;
      const state = {
        minHeight: widget.style.minHeight,
        priority: widget.style.getPropertyPriority('min-height'),
        pending: 0,
        requested: false
      };
      this.loadingWidgets.set(widget, state);
      widget.style.setProperty('min-height', `${widget.getBoundingClientRect().height}px`);
      // Native AJAX starts synchronously in the bubbling handler. A cancelled click
      // or unavailable control should never leave a reservation behind.
      queueMicrotask(() => {
        if (this.loadingWidgets.get(widget) === state && !state.requested) this.releaseHeight(widget, state);
      });
    }

    observeRequests() {
      const $ = window.jdgm?.$;
      if (this.providerDocument || !$) return;
      this.providerDocument = $(document);
      this.onRequest = (_event, request, settings) => {
        const url = new URL(settings.url, location.href);
        if (!url.pathname.endsWith('/reviews_for_widget')) return;
        const params = new URLSearchParams(settings.data);
        const product = params.get('product_id') || url.searchParams.get('product_id');
        for (const [widget, state] of this.loadingWidgets) {
          if (product !== (widget.dataset.id || widget.dataset.productId)) continue;
          const entry = { widget, state, failed: false };
          this.requests.set(request, entry);
          request.fail?.(() => { entry.failed = true; });
          state.pending++;
          state.requested = true;
          break;
        }
      };
      this.onComplete = (_event, request) => {
        const entry = this.requests.get(request);
        if (!entry) return;
        this.requests.delete(request);
        entry.state.pending--;
        if (!entry.state.pending) entry.state.restoreScroll?.();
        // On failure Judge.me leaves the original body hidden. Restore that content.
        if (!entry.state.pending && (entry.failed || (request.status !== 304 && (request.status < 200 || request.status >= 300)))) {
          const body = entry.widget.querySelector('.jdgm-rev-widg__body');
          if (body) $(body).stop(true, true).show();
          $(entry.widget).find('.jdgm-rev-widg__paginate-spinner-wrapper').hide();
        }
        this.scheduleUpdate();
      };
      this.providerDocument.on('ajaxSend', this.onRequest);
      this.providerDocument.on('ajaxComplete', this.onComplete);
    }

    releaseHeight(widget, state) {
      state.restoreScroll?.();
      widget.style.removeProperty('min-height');
      if (state.minHeight) widget.style.setProperty('min-height', state.minHeight, state.priority);
      this.loadingWidgets.delete(widget);
    }

    finishLoading() {
      for (const [widget, state] of this.loadingWidgets) {
        if (!this.contains(widget)) { this.releaseHeight(widget, state); continue; }
        const body = widget.querySelector('.jdgm-rev-widg__body');
        if (!body) { this.releaseHeight(widget, state); continue; }
        // AJAX completion precedes slideDown completion. The existing scoped observer
        // catches its final style cleanup; no polling or guessed animation timeout.
        if (!state.requested || state.pending || getComputedStyle(body).display === 'none' || body.style.height) continue;
        this.releaseHeight(widget, state);
      }
    }

    setDefaultSort(widget) {
      // The legacy widget adds this class after attaching its native change handler.
      // Let Judge.me sort the full dataset and retain that sort during pagination.
      if (this.sortedWidgets.has(widget) || !widget.classList.contains('jdgm--done-setup-widget')) return;
      const select = widget.querySelector('.jdgm-sort-dropdown');
      if (!select?.querySelector('option[value="pictures-first"]')) return;
      const providerWidget = window.jdgm?.$(widget);
      if (!providerWidget) return;
      this.sortedWidgets.add(widget);
      // Preserve links to individual reviews and already-open pagination state.
      if (new URLSearchParams(location.search).has('judgeme_review_uuid') ||
          Number(widget.querySelector('.jdgm-curt[data-page]')?.dataset.page || 1) > 1) return;
      if (select.value === 'pictures-first') return;
      this.preserveHeight(widget);
      const state = this.loadingWidgets.get(widget);
      if (!state) return;
      // Judge.me scrolls before and after sorting. Silence only this initial request.
      const notScrollTo = providerWidget.data('not-scroll-to');
      providerWidget.data('not-scroll-to', true);
      state.restoreScroll = () => {
        if (notScrollTo === undefined) providerWidget.removeData('not-scroll-to');
        else providerWidget.data('not-scroll-to', notScrollTo);
        delete state.restoreScroll;
        delete state.initialSortEvent;
      };
      select.value = 'pictures-first';
      state.initialSortEvent = new Event('change', { bubbles: true });
      select.dispatchEvent(state.initialSortEvent);
    }

    scheduleUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      if (!this.isConnected) return;
      this.observer.disconnect();
      this.observeRequests();
      this.finishLoading();
      this.querySelectorAll('.jdgm-review-widget').forEach(widget => {
        this.setDefaultSort(widget);
        widget.querySelectorAll('.jdgm-rev').forEach(review => {
          const icon = review.querySelector('.jdgm-rev__icon');
          const author = review.querySelector('.jdgm-rev__author')?.textContent.trim();
          if (!icon || !author) return;
          // A reviewer portrait belongs to the provider; never replace it with initials.
          if (icon.querySelector('img, picture') || getComputedStyle(icon).backgroundImage !== 'none') {
            if (icon.hasAttribute('data-lf-initials')) {
              icon.removeAttribute('aria-hidden');
              icon.removeAttribute('data-lf-initials');
            }
            return;
          }
          const words = author.split(/\s+/u);
          const first = Array.from(words[0])[0];
          const last = words.length > 1 ? Array.from(words.at(-1))[0] : '';
          const initials = (first + last).toLocaleUpperCase(document.documentElement.lang || 'en');
          if (icon.textContent !== initials) icon.textContent = initials;
          icon.setAttribute('data-lf-initials', '');
          icon.setAttribute('aria-hidden', 'true');
        });

        const data = widget.querySelector('.jdgm-rev-widg');
        const summary = widget.querySelector('.jdgm-rev-widg__summary-inner');
        const rawRating = data?.getAttribute('data-average-rating');
        const rating = Number(rawRating);
        let label = summary?.querySelector('[data-lf-review-average]');
        if (!summary || !rawRating || !Number.isFinite(rating) || rating < 0 || rating > 5) {
          label?.remove();
          return;
        }
        if (!label) {
          label = document.createElement('span');
          label.className = 'lf-customer-reviews__average';
          label.setAttribute('data-lf-review-average', '');
          label.setAttribute('aria-hidden', 'true');
          summary.append(label);
        }
        label.textContent = `${new Intl.NumberFormat(document.documentElement.lang || 'en', { maximumFractionDigits: 2 }).format(rating)}/5`;
      });
      this.observer.observe(this, {
        childList: true, subtree: true, characterData: true,
        attributes: true, attributeFilter: ['class', 'data-average-rating', 'data-number-of-reviews', 'src', 'style']
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onNavigation, true);
      this.removeEventListener('change', this.onNavigation, true);
      this.providerDocument?.off('ajaxSend', this.onRequest);
      this.providerDocument?.off('ajaxComplete', this.onComplete);
      this.providerDocument = null;
      for (const [widget, state] of this.loadingWidgets) this.releaseHeight(widget, state);
      this.requests.clear();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  });
})();
